import classNames from 'classnames';
import { useSelector } from 'react-redux';
import { Col, Form, OverlayTrigger, Row } from 'react-bootstrap';
import {
  FormCheckbox,
  TextButton,
  DropdownMenu,
  PopoverTooltipV2 as PopoverTooltip,
} from '@praktio/common-ui';
import { useState, useRef, useEffect } from 'react';
import { cloneDeep, debounce, isEqual, sortBy } from 'lodash';

import { RootState } from '../../../ducks/store';
import { SortOptions } from '../../../utils/enums';
import { isPlatformAdmin } from '../../../utils/functions';

import './Tree.scss';

const TriState = {
  All: 'ALL',
  None: 'NONE',
  Some: 'SOME',
};

const setAllNodes = (nodeById: IKey, expanded?: boolean) => {
  const updated: IKey = {};
  Object.keys(nodeById).forEach((k) => {
    if (nodeById[k].children) {
      updated[k] = expanded;
    }
  });

  return updated;
};

const TreeNode = ({
  node,
  expandedNodes,
  selectedNodes,
  onExpandClick,
  onSelectClick,
  renderName,
  depth,
  treeId,
}: {
  node: any;
  expandedNodes: IKey;
  selectedNodes: IKey;
  onExpandClick: (node: any) => void;
  onSelectClick?: (node: any) => void;
  renderName?: (node: any) => JSX.Element;
  depth: number;
  treeId?: string;
}) => {
  const checkboxRef = useRef(null);
  const { name, _id } = node;
  const expanded = expandedNodes[_id];
  const state = selectedNodes[_id] || TriState.None;
  useEffect(() => {
    if (checkboxRef?.current) {
      (checkboxRef.current as any).indeterminate = state === TriState.Some;
    }
  }, [state]);

  const hasChildren = !!node.children?.length;
  const childClassName = `is-child-${depth}`;
  return (
    <div
      className={classNames('TreeNode', {
        'has-children': hasChildren,
        expanded,
        [childClassName]: !!depth,
      })}
    >
      <div className={classNames('TreeNode-spacing')}>
        {!!hasChildren && (
          <span
            onClick={() => onExpandClick(node)}
            className={classNames('material-icons TreeNode-icon')}
          >
            {expanded ? 'expand_more' : 'chevron_right'}
          </span>
        )}
      </div>
      {!!onSelectClick && (
        <Form.Group controlId={_id}>
          <FormCheckbox
            id={`${treeId}${_id}`}
            label={renderName ? renderName(node) : name}
            onChange={() => onSelectClick(node)}
            checked={state === TriState.All}
          />
        </Form.Group>
      )}
      {!onSelectClick && (
        <div className="TreeNode-name">{renderName ? renderName(node) : name}</div>
      )}
    </div>
  );
};

interface ITreeProps {
  data: any[];
  binarySelect?: boolean;
  hideExpandCollapse?: boolean;
  isLocked?: boolean;
  initialSelections?: string[];
  startExpanded?: boolean;
  searchable?: boolean;
  hasClearAll?: boolean;
  id?: string;
  error?: string;
  renderName?: (node: any) => JSX.Element;
  onSelectedChange?: (selectedIds: string[]) => void;
}

/**
 * Sorts the data based on the sortOrder
 * @param data - The tree data to sort
 * @param sortOrder - The sort order to use
 * @returns The sorted data
 */
const sortData = (data: any[], sortOrder: SortOptions) => {
  const nameField = 'name';
  const createdAtField = 'createdAt';

  const nameSorter = (parent: any) => parent.name.toLowerCase();

  if (sortOrder === SortOptions.NAME_ASC) {
    return sortBy(data, [nameSorter], nameField);
  } else if (sortOrder === SortOptions.NAME_DESC) {
    return sortBy(data, [nameSorter], nameField).reverse();
  } else if (sortOrder === SortOptions.CREATED_AT_ASC) {
    return sortBy(data, createdAtField);
  } else if (sortOrder === SortOptions.CREATED_AT_DESC) {
    return sortBy(data, createdAtField).reverse();
  }
};

const Tree = ({
  data,
  binarySelect,
  hideExpandCollapse,
  isLocked = false,
  initialSelections,
  startExpanded,
  searchable,
  hasClearAll,
  id,
  error,
  renderName,
  onSelectedChange,
}: ITreeProps) => {
  let initialSelectedNodes: IKey = {};
  if (initialSelections && data) {
    initialSelections.forEach((id) => {
      initialSelectedNodes[id] = TriState.All;
    });
  }

  const originalDataSet = useRef(false); // Used to determine if the original data has been stored

  const { isAdmin } = useSelector((state: RootState) => ({
    isAdmin: isPlatformAdmin(state.auth.authenticatedUser),
  }));

  const prevData = useRef<any[] | undefined>();
  const [nodeById, setNodeById] = useState<IKey>({});
  const [expandedNodes, setExpandedNodes] = useState<IKey>({});
  const [selectedNodes, setSelectedNodes] = useState<IKey>(initialSelectedNodes);
  const [searchValue, setSearchValue] = useState('');

  const [treeData, setTreeData] = useState<any[]>([]);
  const [orderedNodes, setOrderedNodes] = useState<any[]>([]);
  const [orderedNodesOriginal, setOrderedNodesOriginal] = useState<any[]>([]);
  const [allExpanded, setAllExpanded] = useState(false);
  const [showSortMenu, setShowSortMenu] = useState(false);
  const [searchResultIds, setSearchResultIds] = useState<string[]>([]); // List of ids for search results
  const [hasMultipleTopLevelNodes, setHasMultipleTopLevelNodes] = useState(true);
  const [sortOrder, setSortOrder] = useState<SortOptions>(
    isAdmin ? SortOptions.NAME_ASC : SortOptions.CREATED_AT_DESC,
  );

  // If data or startExpanded changes, reprocess
  useEffect(() => {
    if (!isEqual(data, prevData.current)) {
      const byId: IKey = {};
      const queue = cloneDeep(data);
      let item = queue.shift();
      while (item) {
        const { _id, children } = item;
        byId[_id] = item;
        if (children) {
          queue.unshift(...children);
        }

        item = queue.shift();
      }

      setNodeById(byId);
      if (startExpanded) {
        setExpandedNodes(setAllNodes(byId, true));
        setAllExpanded(true);
      }

      const selections: IKey = {};
      if (initialSelections && data) {
        initialSelections.forEach((id) => {
          selections[id] = TriState.All;
        });
      }

      setSelectedNodes(selections);
    }

    prevData.current = cloneDeep(data);
  }, [data, initialSelections, startExpanded, allExpanded]);

  const onSearchChange = debounce((value) => {
    setSearchValue(value);

    const updated: IKey = {};
    const foundNodes: any[] = [];
    Object.keys(nodeById).forEach((k) => {
      if (nodeById[k].name.toLowerCase().includes(value.toLowerCase())) {
        foundNodes.push(nodeById[k]);
      }
    });

    for (const node of foundNodes) {
      let current = node;
      updated[current._id] = true;
      while (nodeById[current.parent]) {
        current = nodeById[current.parent];
        updated[current._id] = true;
      }
    }

    setExpandedNodes(updated);
    setAllExpanded(true);
  }, 0);

  const onExpandClick = (node: any, expandOverride?: boolean) => {
    const { _id } = node;
    const expanded =
      expandOverride || expandOverride === false ? expandOverride : !expandedNodes[_id];
    setExpandedNodes({ ...expandedNodes, [_id]: expanded });
  };

  const onSelectClick = (node: any) => {
    const updated = cloneDeep(selectedNodes);
    const { _id, parent } = node;
    const state = selectedNodes[_id] !== TriState.All ? TriState.All : TriState.None;
    const selectQueue = [node];
    let selectItem = selectQueue.shift();
    while (selectItem) {
      if (selectItem.children) {
        selectQueue.unshift(...selectItem.children);
      }

      updated[selectItem._id] = state;
      selectItem = selectQueue.shift();
    }

    if (!binarySelect) {
      let parentNode = nodeById[parent];
      let childState = state;
      while (parentNode) {
        // Traverse all children to see if anything is selected
        // and if so, mark accordingly
        const queue = [...parentNode.children];
        let item = queue.shift();
        while (item && childState !== TriState.Some) {
          if (item.children) {
            queue.unshift(...item.children);
          }

          const itemState = updated[item._id];
          if (
            (childState === TriState.All && itemState !== TriState.All) ||
            (childState === TriState.None && itemState && itemState !== TriState.None)
          ) {
            childState = TriState.Some;
          }

          item = queue.shift();
        }

        updated[parentNode._id] = childState;
        if (parentNode.parent) {
          parentNode = nodeById[parentNode.parent];
        } else {
          parentNode = undefined;
        }
      }
    }

    setSelectedNodes(updated);

    const selectedIds = Object.keys(updated).filter(
      (k) => k !== 'All' && updated[k] === TriState.All,
    );

    if (onSelectedChange) {
      onSelectedChange(selectedIds);
    }
  };

  const handleClearAll = () => {
    setSelectedNodes({});

    if (onSelectedChange) {
      onSelectedChange([]);
    }
  };

  const handleSelectAll = () => {
    const updated: IKey = {};

    Object.keys(nodeById).forEach((k) => {
      updated[k] = TriState.All;
    });

    setSelectedNodes(updated);

    if (onSelectedChange) {
      onSelectedChange(Object.keys(nodeById));
    }
  };

  const handleSortClick = (sortOption: SortOptions) => {
    setSortOrder(sortOption);
    setShowSortMenu(false);
  };

  // Set treeData to data when data changes
  useEffect(() => {
    setTreeData(data);
  }, [data]);

  // Sort treeData when sortOrder changes
  useEffect(() => {
    const sortedData = sortData(treeData, sortOrder) as any[];
    setTreeData(sortedData);

    // We don't want to re-sort the data when the treeData changes
    // eslint-disable-next-line
  }, [sortOrder]);

  useEffect(() => {
    let sortedData = sortData(treeData, sortOrder) as any[];

    let queue = cloneDeep(sortedData);
    let item = queue.shift();
    const depthById: IKey = {};
    let updatedOrderedNodes: any = [];
    const collapsedParents: IKey = {};

    while (item) {
      const { _id, children, parent } = item;

      // Do null coalescing (?? -1) here because if the
      // parent isn't found, it should be top level (0)
      const depth = parent ? (depthById[parent] ?? -1) + 1 : 0;
      depthById[_id] = depth;

      // Render the node if it has no parent/parent doesn't exist, or it's parent is expanded
      if (!parent || !nodeById[parent] || (expandedNodes[parent] && !collapsedParents[parent])) {
        updatedOrderedNodes.push({ ...item, depth });
      } else {
        collapsedParents[_id] = true;
      }

      if (children) {
        queue.unshift(...children);
      }

      item = queue.shift();
    }

    // If searchValue, filter out any nodes that aren't in the search results id list
    if (searchValue) {
      updatedOrderedNodes = updatedOrderedNodes.filter((node: any) =>
        searchResultIds.includes(node._id as string),
      );
    }

    setOrderedNodes(updatedOrderedNodes);

    // Save the original nodes on load
    if (!originalDataSet.current && updatedOrderedNodes.length) {
      setOrderedNodesOriginal(updatedOrderedNodes);

      setHasMultipleTopLevelNodes(
        updatedOrderedNodes.filter((node: any) => node.depth === 0).length > 1,
      );

      originalDataSet.current = true;
    }

    // We don't need this to run when sortOrder changes
    // eslint-disable-next-line
  }, [treeData, nodeById, expandedNodes]);

  useEffect(() => {
    let queue = cloneDeep(orderedNodesOriginal);
    let item = queue.pop();
    let lastAddedItem = undefined;

    const updatedOrderedNodes: any[] = [];

    while (item) {
      if (
        item.name.toLowerCase().includes(searchValue.toLowerCase()) ||
        (lastAddedItem?.parent && lastAddedItem.parent === item._id)
      ) {
        updatedOrderedNodes.unshift(item);
        lastAddedItem = item;
      }

      item = queue.pop();
    }

    const searchResultTeamIds = updatedOrderedNodes.map((node) => node._id); // Save the ids of the search results to use in filtering

    setSearchResultIds(searchResultTeamIds);
    setOrderedNodes(updatedOrderedNodes);

    // eslint-disable-next-line
  }, [searchValue]);

  // Close the sort menu when clicking outside of it
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      const targetEl = e.target as HTMLElement;

      if (!showSortMenu || targetEl.closest('.TextButton') || targetEl.closest('.dropdown-item')) {
        return;
      }

      setShowSortMenu(false);
    };

    document.addEventListener('click', handleClick);

    return () => {
      document.removeEventListener('click', handleClick);
    };
  }, [showSortMenu]);

  const actionBar = [];

  if (!hideExpandCollapse) {
    actionBar.push(
      <div
        className="expand-collapse-all"
        onClick={() => {
          setExpandedNodes(setAllNodes(nodeById, !allExpanded));
          setAllExpanded(!allExpanded);
        }}
      >
        <span>{!allExpanded ? 'Expand' : 'Collapse'}</span>
      </div>,
    );
  }

  if (hasClearAll) {
    const noSelectedItems = Object.keys(selectedNodes).length === 0;

    actionBar.push(
      <div className="clear-all" onClick={noSelectedItems ? handleSelectAll : handleClearAll}>
        <span>{noSelectedItems ? 'Select All' : 'Clear All'}</span>
      </div>,
    );
  }

  const renderSortButtonText = () => {
    return (
      <strong>
        Sort
        {hasMultipleTopLevelNodes ? (
          <>
            : <span>{sortOrder}</span>
          </>
        ) : null}
      </strong>
    );
  };

  return (
    <>
      <div
        className={classNames('Tree', {
          'is-locked': isLocked,
        })}
      >
        <Row>
          <div className="Tree__SortButton">
            <TextButton
              disabled={!!searchValue || !hasMultipleTopLevelNodes}
              icon="sort"
              onClick={() => setShowSortMenu((prev) => !prev)}
            >
              {hasMultipleTopLevelNodes ? (
                renderSortButtonText()
              ) : (
                <OverlayTrigger
                  placement="right"
                  overlay={
                    <PopoverTooltip
                      id="tooltip-sort"
                      paragraph="Only available for multiple top-level teams"
                    />
                  }
                >
                  {renderSortButtonText()}
                </OverlayTrigger>
              )}
            </TextButton>

            <DropdownMenu
              menuOnly={showSortMenu}
              options={[
                {
                  id: '1',
                  label: SortOptions.NAME_ASC,
                  onClick: () => handleSortClick(SortOptions.NAME_ASC),
                },
                {
                  id: '2',
                  label: SortOptions.NAME_DESC,
                  onClick: () => handleSortClick(SortOptions.NAME_DESC),
                },
                {
                  id: '3',
                  label: SortOptions.CREATED_AT_ASC,
                  onClick: () => handleSortClick(SortOptions.CREATED_AT_ASC),
                },
                {
                  id: '4',
                  label: SortOptions.CREATED_AT_DESC,
                  onClick: () => handleSortClick(SortOptions.CREATED_AT_DESC),
                },
              ]}
            />
          </div>

          <div className="Tree__ActionBar">{actionBar}</div>
        </Row>

        <Row>
          <Col key="searchbar" xs={!hideExpandCollapse || hasClearAll ? 4 : 12}>
            {!!searchable && (
              <Form.Control
                placeholder="Search..."
                value={searchValue}
                onChange={(event: any) => {
                  onSearchChange(event?.target.value);
                }}
              />
            )}
          </Col>
        </Row>

        <div className="Tree-content">
          {orderedNodes.map((n: any) => (
            <TreeNode
              key={`${id || ''}${n._id}`}
              expandedNodes={expandedNodes}
              selectedNodes={selectedNodes}
              node={n}
              onExpandClick={onExpandClick}
              onSelectClick={onSelectedChange ? onSelectClick : undefined}
              renderName={renderName}
              depth={n.depth}
              treeId={id}
            />
          ))}
        </div>
      </div>

      {!!error && <div className="PraktioFeedback invalid-feedback">{error}</div>}
    </>
  );
};

export default Tree;
